import { FC, useEffect, useMemo, useState } from 'react';
import { useForm, Validate } from 'react-hook-form';

import { parseValidEnumeration } from '@/components/search/v1/utils/searchValidations';
import { defaultFilter, type DefectenFilter } from '@/components/sidebar/defecten/defectenFilter/defectenFilterModel';
import SortField from '@/components/sidebar/sortField/SortField';
import {
  getSortDefectenOptions,
  resolveSorting,
  SorteringRichting,
  type SorteringType,
  useSorting,
} from '@/components/sidebar/sortField/useSorting';
import { Checkbox } from '@/components/UI/Checkbox/Checkbox';
import MwdDialog from '@/components/UI/Dialog/Dialog';
import { InputField } from '@/components/UI/Fields/ReactFormFields/CreateFieldComponent';
import MaterieelTypeSelectField from '@/components/UI/Fields/ReactFormFields/MaterieelTypeSelectField';
import { Label } from '@/components/UI/Label/Label';
import type { BronType, DefectSoort, DefectStatus } from '@/types/Defecten';

import styles from './DefectenFilterDialog.module.css';
import inputFieldStyles from '@/components/UI/Fields/Fields.module.css';

export { defaultFilter };
export type { DefectenFilter };

const sortDefectenOptions = getSortDefectenOptions();
const defaultSortType = sortDefectenOptions[0];
const defaultValueSortDirection = SorteringRichting.UP;

export type DefectenFilterDialogProps = {
  showDialog: boolean;
  handleOpenChange: (p: boolean) => void;
  setNieuwFilter: (filter: DefectenFilter) => void;
  huidigFilter: DefectenFilter;
};

export type FilterDefectenFormProps = {
  eenheidnummer: string;
  materieelTypen: string[] | null;
  sorteringType: SorteringType;
  sorteringRichting: SorteringRichting;
};

const eenheidNummerRegex = /^[\d]{3,14}(-[\d]{3,14})?$/; // NOSONAR
const eenheidReeksRegex = /^([\d]{1,14}-)+[\d]{1,14}$/; // NOSONAR

const valideerEenheidnummer: (value: string) => boolean | string = (value) => {
  return value === '' || eenheidNummerRegex.test(value) || eenheidReeksRegex.test(value) || parseValidEnumeration(value)
    ? true
    : 'Invoer is geen eenheidnummer, reeks of opsomming';
};

const DefectCheckboxItem: FC<{
  testId: string;
  label: string;
  onChange: (checked: boolean) => void;
  value: boolean;
}> = ({ testId, label, onChange, value }) => (
  <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
    <Checkbox data-testid={testId} onChange={onChange} value={value} />
    <span className={styles.defectCheckboxStyle} onClick={() => onChange(!value)} role='button'>
      {label}
    </span>
  </div>
);

export const DefectenFilterDialog = ({
  showDialog,
  handleOpenChange,
  huidigFilter,
  setNieuwFilter,
}: DefectenFilterDialogProps) => {
  const { handleSubmit, reset, control, setValue } = useForm<FilterDefectenFormProps>({
    mode: 'onSubmit',
  });

  const { sorteringType: initialValueSortType, sorteringRichting: initialValueSortDirection } = useMemo(
    () => resolveSorting(huidigFilter, defaultFilter),
    [huidigFilter.sorteringRichting, huidigFilter.sorteringType],
  );

  const {
    handleSortChange,
    handleSortDirectionChange,
    selectedSortType,
    selectedSortDirection,
    setSelectedSortDirection,
    setSelectedSortType,
  } = useSorting(initialValueSortType, initialValueSortDirection);

  useEffect(() => {
    if (selectedSortDirection) {
      setValue('sorteringRichting', selectedSortDirection);
    }
  }, [selectedSortDirection, setValue]);

  useEffect(() => {
    if (selectedSortType) {
      setValue('sorteringType', selectedSortType);
    }
  }, [selectedSortType, setValue]);

  const initialValueInzetbaarheid = useMemo(() => huidigFilter.defectSoorten ?? [], [huidigFilter.defectSoorten]);
  const [inzetbaarheidCheckboxes, setInzetbaarheidCheckboxes] = useState(initialValueInzetbaarheid);

  const handleInzetbaarheidChange = (type: DefectSoort, checked: boolean) => {
    setInzetbaarheidCheckboxes((current) => (checked ? [...current, type] : [...current.filter((v) => v !== type)]));
  };

  const initialValueDefectStatus = useMemo(() => huidigFilter.defectStatussen ?? [], [huidigFilter.defectStatussen]);
  const [defectStatusCheckboxes, setDefectStatusCheckboxes] = useState(initialValueDefectStatus);

  const handleDefectStatusChange = (type: DefectStatus, checked: boolean) => {
    setDefectStatusCheckboxes((current) => (checked ? [...current, type] : [...current.filter((v) => v !== type)]));
  };
  const initialValueDefectBronType = useMemo(() => huidigFilter.defectBronnen ?? [], [huidigFilter.defectBronnen]);
  const [defectBronCheckboxes, setDefectBronCheckboxes] = useState(initialValueDefectBronType);

  const handleDefectBronChange = (type: BronType, checked: boolean) => {
    setDefectBronCheckboxes((current) => (checked ? [...current, type] : [...current.filter((v) => v !== type)]));
  };

  const onSubmit = async (formData: FilterDefectenFormProps) => {
    const kandidaatFilter: DefectenFilter = {
      eenheidnummer: formData.eenheidnummer,
      materieelTypen:
        formData.materieelTypen && formData.materieelTypen.length !== 0 ? formData.materieelTypen : undefined,
      defectSoorten: inzetbaarheidCheckboxes.length === 0 ? undefined : inzetbaarheidCheckboxes,
      defectStatussen: defectStatusCheckboxes.length === 0 ? undefined : defectStatusCheckboxes,
      defectBronnen: defectBronCheckboxes.length === 0 ? undefined : defectBronCheckboxes,
      sorteringRichting: formData.sorteringRichting,
      sorteringType: formData.sorteringType,
    };

    if (
      Object.values(kandidaatFilter).every(
        (el) => el === undefined || el === '' || el === defaultValueSortDirection || el === defaultSortType.value,
      )
    ) {
      setNieuwFilter(defaultFilter);
    } else {
      setNieuwFilter(kandidaatFilter);
    }
    setTimeout(() => {
      handleOpenChange(false);
    }, 100);
  };

  return (
    <MwdDialog
      title='Filter defecten'
      className={styles.defectenFilterenDialogStyle}
      showDialog={showDialog}
      handleOpenChange={handleOpenChange}
      data-testid='filterDefecten'
      hasCloseInHeader
      headerSeparator
      paddingTopChildren={false}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <SortField
          name='sorteringType'
          control={control}
          options={sortDefectenOptions}
          defaultValue={huidigFilter.sorteringType ?? defaultSortType.label}
          onChangeCallback={(value) => handleSortChange(value as SorteringType)}
          onSortDirectionChangeCallback={handleSortDirectionChange}
          selectedSortDirection={selectedSortDirection}
        />
        <div style={{ paddingBottom: '16px' }}>
          <InputField
            className={`${inputFieldStyles.inputField} ${styles.eenheidFilterInputStyle}`}
            autoComplete='off'
            control={control}
            data-testid='defect-eenheidnummer'
            placeholder='Filter op eenheidnummer of reeks'
            maxLength={255}
            name='eenheidnummer'
            label='Eenheidnummer of reeks'
            rules={{
              validate: {
                eenheidnummer: valideerEenheidnummer as Validate<string | string[] | null, FilterDefectenFormProps>,
              },
            }}
            showOptional={false}
            defaultValue={huidigFilter.eenheidnummer ?? ''}
          />
        </div>

        <MaterieelTypeSelectField
          name='materieelTypen'
          control={control}
          defaultValue={(huidigFilter.materieelTypen ?? []).map((v) => ({ label: v, value: v }))}
        />

        <div>
          <Label>
            <div
              style={{ paddingTop: '4px', paddingBottom: '4px', display: 'flex', alignItems: 'baseline', gap: '8px' }}
            >
              Inzetbaarheid
            </div>
          </Label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <DefectCheckboxItem
              label='Niet inzetbaar'
              testId='niet-inzetbaar'
              onChange={(isChecked) => handleInzetbaarheidChange('DEFECT_NIET_INZETBAAR', isChecked)}
              value={inzetbaarheidCheckboxes.includes('DEFECT_NIET_INZETBAAR')}
            />
            <DefectCheckboxItem
              label='Beperkt'
              testId='beperkt-inzetbaar'
              onChange={(isChecked) => handleInzetbaarheidChange('BEPERKING_IN_TREINDIENST', isChecked)}
              value={inzetbaarheidCheckboxes.includes('BEPERKING_IN_TREINDIENST')}
            />
            <DefectCheckboxItem
              label='Geen beperking'
              testId='geen-beperking-inzetbaar'
              onChange={(isChecked) => handleInzetbaarheidChange('GEEN_BEPERKINGEN', isChecked)}
              value={inzetbaarheidCheckboxes.includes('GEEN_BEPERKINGEN')}
            />
          </div>
        </div>

        <div>
          <Label>
            <div
              style={{ paddingTop: '16px', paddingBottom: '4px', display: 'flex', alignItems: 'baseline', gap: '8px' }}
            >
              Status defect
            </div>
          </Label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <DefectCheckboxItem
              label='Openstaand'
              testId='defect-openstaand'
              onChange={(isChecked) => handleDefectStatusChange('OPENSTAAND', isChecked)}
              value={defectStatusCheckboxes.includes('OPENSTAAND')}
            />
            <DefectCheckboxItem
              label='Gereed'
              testId='defect-gereed-gemeld'
              onChange={(isChecked) => handleDefectStatusChange('GEREED_GEMELD', isChecked)}
              value={defectStatusCheckboxes.includes('GEREED_GEMELD')}
            />
          </div>
        </div>

        <div>
          <Label>
            <div
              style={{ paddingTop: '16px', paddingBottom: '4px', display: 'flex', alignItems: 'baseline', gap: '8px' }}
            >
              Bron
            </div>
          </Label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <DefectCheckboxItem
              label='Handmatig'
              testId='defect-handmatig'
              onChange={(isChecked) => handleDefectBronChange('HANDMATIG', isChecked)}
              value={defectBronCheckboxes.includes('HANDMATIG')}
            />
          </div>
        </div>

        <MwdDialog.Footer
          submitCaption='Filter defecten'
          cancelCallback={() => {
            reset({
              eenheidnummer: '',
              materieelTypen: null,
              sorteringType: defaultSortType.value,
              sorteringRichting: defaultValueSortDirection,
            });
            setSelectedSortType(defaultSortType.value);
            setSelectedSortDirection(defaultValueSortDirection);
            setInzetbaarheidCheckboxes([]);
            setDefectStatusCheckboxes([]);
            setDefectBronCheckboxes([]);
            setNieuwFilter(defaultFilter);
            handleOpenChange(false);
          }}
          cancelCaption='Verwijder criteria'
          justifyContentStyle='space-between'
        />
      </form>
    </MwdDialog>
  );
};
